import React, { useState } from "react";
import { Button, Dialog, Textarea } from "@/components/ui";
import { Media, SuperPageAssignment } from "@/services/bedrock";
import { SuperPageServices } from "@/services/bedrock/SuperPageServices";
import { SuperPageIcon } from "../SuperPageIcon";
import { useSocialCoachSession } from "@/utils";
import { VideoPageSourceModal } from "@/containers/VideoPages";
import { cn } from "@/lib/utils";
import { ArrowRight, Clapperboard } from "lucide-react";

type Props = {
  superPageAssignment: SuperPageAssignment;
  onMediaSelected: (media: Media) => void;
  onUseDefaultVideo: (superPageAssignment: SuperPageAssignment) => void;
};
export const SuperPageAssignmentDialogContent: React.FC<Props> = ({
  superPageAssignment,
  onMediaSelected,
  onUseDefaultVideo,
}) => {
  const [showInlineDefaultVideo, setShowInlineDefaultVideo] = useState(false);
  const [videoPageSourceModalOpen, setVideoPageSourceModalOpen] = useState(false);
  const { isPlayer } = useSocialCoachSession();

  const superPage = superPageAssignment.superPage;
  const scriptBody = superPage?.videoScript?.content?.trim();
  const defaultVideoUri = superPage?.templateContentPage?.originalMedia?.[0]?.uri;
  const hasAssignmentDetails = !!(superPage?.videoScript || defaultVideoUri);
  const isAvailable = superPageAssignment.status === "AVAILABLE";

  const useDefaultVideo = async () => {
    const updatedAssignment = await SuperPageServices.updateSuperPageAssignment(
      superPageAssignment.superPageId,
      superPageAssignment.id,
      { useDefaultVideo: true },
      { superPage: ["videoScript", "templateContentPage"] }
    );
    onUseDefaultVideo(updatedAssignment);
  };

  return (
    <Dialog.Content
      className={cn("flex min-h-0 flex-col overflow-y-auto p-6", hasAssignmentDetails ? "h-full gap-8" : "gap-6")}
    >
      <div className="flex flex-col gap-4">
        <div className="flex gap-4">
          <SuperPageIcon size="lg" />
          <div className="min-w-0 flex flex-col gap-1.5 pr-14">
            <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase ignore-base-styling">
              Video Page Assignment
            </span>
            <Dialog.Title className="text-left text-xl font-bold leading-snug text-foreground sm:text-2xl">
              {superPage?.title?.trim() || "No title"}
            </Dialog.Title>
          </div>
        </div>
        {isPlayer && (
          <Dialog.Description className="text-base leading-relaxed text-muted-foreground">
            {isAvailable
              ? "This Video Page assignment is available to you whenever you're ready. "
              : "Your team has requested a new Video Page from you. "}
            {!!defaultVideoUri ? "You can record a personalized version, or use the pre-recorded default." : ""}
          </Dialog.Description>
        )}
      </div>

      {hasAssignmentDetails ? (
        <>
          <div className="flex min-h-0 gap-8 flex-1">
            {superPage?.videoScript && (
              <div className="flex h-full min-h-0 flex-1 flex-col gap-3">
                <h3 className="shrink-0 text-center text-base font-bold text-foreground m-0">
                  {isPlayer ? "Your Script" : "Script"}
                </h3>
                <Textarea className="min-h-0 flex-1 resize-none no-scrollbar" value={scriptBody} readOnly />
              </div>
            )}
            {!!defaultVideoUri && (
              <div className="flex h-full min-h-0 flex-1 flex-col gap-3">
                <h3 className="shrink-0 text-center text-base font-bold text-foreground m-0">Default Video</h3>
                <div className="relative mx-auto w-full max-w-[220px]">
                  <div className="relative aspect-[9/16] w-full overflow-hidden rounded-xl bg-muted shadow-md">
                    {showInlineDefaultVideo && defaultVideoUri ? (
                      <video src={defaultVideoUri} controls autoPlay className="size-full object-cover" />
                    ) : (
                      <>
                        <img
                          src={superPage?.templateContentPage?.previewMedia?.[0]?.uri ?? defaultVideoUri}
                          alt=""
                          className="size-full object-cover cursor-pointer"
                          onClick={() => {
                            if (defaultVideoUri) {
                              setShowInlineDefaultVideo(true);
                            }
                          }}
                        />
                        <div className="absolute right-0 bottom-0 left-0 border-t border-sky-500/80 bg-white/95 px-2 py-1.5 text-center text-[11px] font-semibold leading-tight text-foreground">
                          {superPage?.title?.trim() || "Default video"}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {isPlayer && (
                  <p className="text-center text-sm text-muted-foreground m-0">
                    Short on time? You don't have to record your own.
                  </p>
                )}
                <Button type="button" variant="link" className="self-center" onClick={useDefaultVideo}>
                  Use Default Video
                </Button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Button type="button" onClick={() => setVideoPageSourceModalOpen(true)}>
              Add a video
            </Button>
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={() => setVideoPageSourceModalOpen(true)}
          className={cn(
            "group flex w-full items-center gap-4 rounded-xl border-2 border-dashed border-primary/25 bg-primary/5 p-4 text-left font-sans transition-all cursor-pointer",
            "hover:border-primary/50 hover:bg-primary/10 hover:shadow-sm",
            "focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary/20"
          )}
        >
          <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
            <Clapperboard className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="m-0 text-base font-semibold text-foreground">Add your video</p>
            <p className="m-0 mt-0.5 text-sm text-muted-foreground">
              Record yourself or upload a file to create your Video Page.
            </p>
          </div>
          <ArrowRight className="size-5 shrink-0 text-primary/60 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
        </button>
      )}
      <VideoPageSourceModal
        open={videoPageSourceModalOpen}
        title="Video Page Assignment"
        hideScriptSelection
        videoScript={superPage?.videoScript}
        onOpenChange={setVideoPageSourceModalOpen}
        onMediaSelected={onMediaSelected}
      />
    </Dialog.Content>
  );
};
